import { HttpError, MICROSOFT_OAUTH_SETTINGS } from "@anydo/core";
import { IAnydoElectronPreload } from "@anydo/interfaces";
import { DefaultCrypto } from "@openid/appauth";

declare var AnydoElectronPreload: IAnydoElectronPreload | void;

const crypto = new DefaultCrypto();

async function getMicrosoftOneDriveAccessToken({ scopes, signal }: { scopes: string[]; signal?: AbortSignal }) {
  const codeVerifier = crypto.generateRandom(128);
  const challenge = await crypto.deriveChallenge(codeVerifier);
  const redirectUri = window.location.origin + `/auth/microsoft.html`;

  const url = `https://login.microsoftonline.com/consumers/oauth2/v2.0/authorize?${new URLSearchParams({
    prompt: "select_account",
    client_id: MICROSOFT_OAUTH_SETTINGS.clientId,
    response_type: "code",
    redirect_uri: redirectUri,
    response_mode: "fragment",
    scope: scopes.join(" "),
    state: "",
    code_challenge: challenge,
    code_challenge_method: "S256",
  })}`;

  const win = window.open(url);
  if (!win) {
    throw new Error("getMicrosoftAccessToken: failed to open authentication popup");
  }

  const { resolve, reject, promise } = Promise.withResolvers<string>();

  function handlePostMessage(evt: MessageEvent) {
    if (evt.source !== win) {
      return;
    }

    switch (evt.data?.type) {
      case "code":
        win?.close();
        window.removeEventListener("message", handlePostMessage);

        const code = evt.data.code;

        fetch("https://login.microsoftonline.com/consumers/oauth2/v2.0/token", {
          method: "post",
          body: new URLSearchParams({
            client_id: MICROSOFT_OAUTH_SETTINGS.clientId,
            scope: scopes.join(" "),
            code,
            redirect_uri: redirectUri,
            grant_type: "authorization_code",
            code_verifier: codeVerifier,
          }),
        })
          .then(async (res) => {
            if (!res.ok) {
              throw new HttpError(res, 0, await res.text());
            }

            const data = (await res.json()) as { access_token: string };
            resolve(data.access_token);
          })
          .catch(reject);

        break;
      case "error":
        win?.close();
        window.removeEventListener("message", handlePostMessage);
        reject(evt.data.error);
    }
  }

  window.addEventListener("message", handlePostMessage, { signal });
  return promise;
}

export async function getOneDriveAccessToken(signal?: AbortSignal) {
  const scopes = ["onedrive.readonly", "offline_access"];

  return typeof AnydoElectronPreload !== "undefined" ?
      await AnydoElectronPreload.thirdPartyAuth.getMicrosoftOneDriveAccessToken({
        scopes,
      })
    : await getMicrosoftOneDriveAccessToken({
        scopes,
        signal,
      });
}
